// Libraries
import { Card, Flex, Button, Center, Text } from '@chakra-ui/react';
import Link from 'next/link';
// Components
import { CompassIcon } from '@atoms/index';
// Types
import { ErrorCardProps } from './ErrorCard.types';
// Utils
import { AppPath } from '@utils/index';
import text from '@translations/en.json';

/**
 * ErrorCard component displays an error message with a title, description, and a button.
 *
 * @component
 * @param {string} props.title - The title of the error message.
 * @param {string} props.description - The description of the error message.
 * @param {string} props.hiddenButton - A flag used to hide the action button.
 * @param {React.ComponentType} [props.buttonAs=Link] - The component to use for the button. Defaults to the Next.js Link component.
 * @returns {React.Component} - A React component representing the error card.
 */
export const ErrorCard = ({ title, description, buttonAs = Link, hiddenButton = false }: ErrorCardProps) => (
  <Center h='calc(100vh - 3rem)' flex={1} py={6}>
    <Card px={4} mx={4} maxW={{ base: 100, md: 'fit-content' }}>
      <Flex direction={{ base: 'column', md: 'row' }}>
        <Flex direction='column' my='auto' flex={1} align={{ base: 'center', md: 'start' }} mx={{ md: 12 }}>
          <Text fontSize={{ base: 'hxl', md: 'h2xl' }} fontWeight='bold' mt={4} textAlign='center'>
            {title}
          </Text>
          {typeof description === 'string' ? (
            <Text fontSize={{ base: 'xl', md: 'hxl' }} mt={4} textAlign='center'>
              {description}
            </Text>
          ) : (
            description.map((desc, index) => (
              <Text key={index} fontSize={{ base: 'xl', md: 'hxl' }} mt={4} textAlign='center'>
                {desc}
              </Text>
            ))
          )}
          {!hiddenButton && (
            <Button
              as={buttonAs}
              href={AppPath.HOME}
              bg='submitScheme.500'
              color='white'
              mt={{ base: 6, md: 8 }}
              _hover={{ bg: 'submitScheme.600' }}
            >
              {text.error_card.button}
            </Button>
          )}
        </Flex>
        <Center p={{ base: 4, md: 8 }} my={1}>
          <CompassIcon w='full' maxW={100} h='full' />
        </Center>
      </Flex>
    </Card>
  </Center>
);
